import React, { useEffect, useRef, useState } from 'react';
import service, { newHelpId } from '../services/helpCenterService';
import { useT } from '../i18n/LanguageContext';
import { captureSession, isSessionCurrent, SESSION_EVENT } from '../utils/authSession';

// Memory only. Navigation preserves drafts; a logout/account change destroys them.
const drafts = new Map();
const draftListeners = new Set();
const notifyDraft = (key) => draftListeners.forEach((listener) => listener(key));
const revoke = (draft) =>
  draft?.files?.forEach((item) => {
    item.controller?.abort();
    if (item.preview) URL.revokeObjectURL(item.preview);
  });
function clearDrafts() {
  drafts.forEach(revoke);
  drafts.clear();
}
window.addEventListener(SESSION_EVENT, clearDrafts);
// Drafts survive route changes, so the warning must survive composer unmounts too.
window.addEventListener('beforeunload', (event) => {
  if (
    [...drafts.values()].some(
      (draft) =>
        isSessionCurrent(draft.session) &&
        (draft.text || draft.subject || draft.files.length || draft.pending)
    )
  ) {
    event.preventDefault();
    event.returnValue = '';
  }
});
window.addEventListener('storage', (event) => {
  if (!event.key || ['medeffects.authSession', 'user', 'accessToken'].includes(event.key)) {
    drafts.forEach((draft, key) => {
      if (!isSessionCurrent(draft.session)) {
        revoke(draft);
        drafts.delete(key);
      }
    });
  }
});
const blank = () => ({ subject: '', category: 'other', text: '', files: [], pending: null });
export const helpCategories = {
  orders: 'Orders & shipping',
  billing: 'Billing',
  account: 'Account access',
  technical: 'Technical issue',
  other: 'Other',
};

export function clearHelpDrafts(keepIdentity) {
  drafts.forEach((draft, key) => {
    if (!keepIdentity || !key.startsWith(`${keepIdentity}:`)) {
      revoke(draft);
      drafts.delete(key);
    }
  });
}

export default function HelpComposer({
  draftKey,
  creating,
  config,
  onSend,
  canResolve = false,
  expectedRevision,
}) {
  const t = useT();
  const [draft, setDraft] = useState(() => drafts.get(draftKey) || blank());
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const mounted = useRef(true);
  const current = useRef(draft);
  current.current = draft;
  const input = useRef();
  const session = useRef(captureSession());
  const sendingRef = useRef(false);
  const uploadQueue = useRef(Promise.resolve());
  const update = (change) => {
    const old = current.current;
    const next = {
      ...(typeof change === 'function' ? change(old) : { ...old, ...change }),
      session: session.current,
    };
    current.current = next;
    drafts.set(draftKey, next);
    setDraft(next);
  };
  useEffect(() => {
    mounted.current = true;
    const sync = (key) => {
      if (key !== draftKey || !isSessionCurrent(session.current)) return;
      const next = drafts.get(key) || blank();
      current.current = next;
      setDraft(next);
      setError(next.failure || '');
      if (!next.pending) {
        sendingRef.current = false;
        setSending(false);
      }
    };
    draftListeners.add(sync);
    return () => {
      mounted.current = false;
      draftListeners.delete(sync);
      current.current.files.forEach((f) => {
        if (['queued', 'uploading'].includes(f.status)) {
          f.controller?.abort();
          f.status = 'failed';
        }
      });
    };
  }, [draftKey]);
  const live = () => mounted.current && isSessionCurrent(session.current);
  const patchFile = (id, patch) => {
    if (live())
      update((old) => ({
        ...old,
        files: old.files.map((file) => (file.id === id ? { ...file, ...patch } : file)),
      }));
  };
  const upload = async (item) => {
    const controller = new AbortController();
    patchFile(item.id, { status: 'uploading', progress: 0, controller });
    try {
      const attachment = await service.upload(
        item.file,
        item.id,
        (progress) => patchFile(item.id, { progress }),
        controller.signal
      );
      patchFile(item.id, { status: 'ready', attachment, progress: 100 });
    } catch {
      patchFile(item.id, { status: 'failed' });
    }
  };
  const enqueueUpload = (item) => {
    patchFile(item.id, { status: 'queued', progress: 0 });
    uploadQueue.current = uploadQueue.current.then(async () => {
      if (live() && current.current.files.some((f) => f.id === item.id)) await upload(item);
    });
  };
  const selectFiles = (selected) => {
    if (
      config?.writesEnabled === false ||
      config?.readOnly ||
      !config?.uploadsEnabled ||
      sendingRef.current ||
      current.current.pending
    )
      return;
    const incoming = Array.from(selected);
    const max = config?.limits?.attachments || 5;
    if (incoming.length + current.current.files.length > max) {
      setError(t('Choose up to {count} attachments.', { count: max }));
      return;
    }
    if (
      incoming.some(
        (file) =>
          !/\.(jpe?g|png|pdf)$/i.test(file.name) ||
          !['image/jpeg', 'image/png', 'application/pdf'].includes(file.type) ||
          file.size > (config?.limits?.fileBytes || 5242880) ||
          !file.size
      )
    ) {
      setError(
        t(
          'Use JPG, PNG, or PDF files up to 5 MB each. HEIC photos must first be converted to JPG or PNG.'
        )
      );
      return;
    }
    const added = incoming.map((file) => ({
      id: newHelpId(),
      file,
      status: 'queued',
      progress: 0,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
    }));
    setError('');
    update((old) => ({ ...old, files: [...old.files, ...added] }));
    // Sequential uploads bound memory and server concurrency.
    added.forEach(enqueueUpload);
  };
  const send = async (resolve = false) => {
    if (!live() || config?.writesEnabled === false || config?.readOnly || sendingRef.current)
      return;
    if (
      !draft.pending &&
      ((!draft.text.trim() && !draft.files.length) ||
        (creating && !draft.subject.trim()) ||
        draft.files.some((f) => f.status !== 'ready'))
    ) {
      setError(t('Add your message or a ready attachment, and complete all required fields.'));
      return;
    }
    const pending = draft.pending || {
      text: draft.text.trim(),
      attachmentIds: draft.files.map((f) => f.attachment._id),
      clientMessageId: newHelpId(),
      ...(creating
        ? { subject: draft.subject.trim(), category: draft.category }
        : { resolve, ...(resolve ? { expectedRevision } : {}) }),
    };
    update({ pending, failure: '' });
    sendingRef.current = true;
    setSending(true);
    setError('');
    try {
      await onSend(pending);
      if (
        isSessionCurrent(session.current) &&
        drafts.get(draftKey)?.pending?.clientMessageId === pending.clientMessageId
      ) {
        revoke(drafts.get(draftKey));
        drafts.delete(draftKey);
        notifyDraft(draftKey);
      }
    } catch (err) {
      if (
        isSessionCurrent(session.current) &&
        drafts.get(draftKey)?.pending?.clientMessageId === pending.clientMessageId
      ) {
        const status = err.response?.status;
        // A timeout or server failure may have persisted. Freeze exact content/UUID for reconciliation.
        const cached = drafts.get(draftKey);
        drafts.set(draftKey, {
          ...cached,
          pending: status && status < 500 && status !== 408 ? null : cached.pending,
          failure: t(
            status === 409
              ? 'This request changed. Refresh the conversation before trying again. Your draft is preserved.'
              : 'Message not confirmed. Your draft is preserved. Retry to safely check and send the same message.'
          ),
        });
        notifyDraft(draftKey);
      }
    } finally {
      sendingRef.current = false;
      if (live()) setSending(false);
    }
  };
  const disabled = config?.writesEnabled === false || config?.readOnly;
  const locked = disabled || sending || Boolean(draft.pending);
  return (
    <section
      className="hc-composer"
      aria-label={t(creating ? 'New request' : 'Reply')}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        selectFiles(event.dataTransfer.files);
      }}
    >
      {creating && (
        <div className="hc-create-fields">
          <label>
            {t('Subject')}
            <input
              maxLength={120}
              value={draft.subject}
              disabled={locked}
              onChange={(e) => update({ subject: e.target.value })}
              required
            />
          </label>
          <label>
            {t('Category')}
            <select
              value={draft.category}
              disabled={locked}
              onChange={(e) => update({ category: e.target.value })}
            >
              {Object.entries(helpCategories).map(([value, label]) => (
                <option value={value} key={value}>
                  {t(label)}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      <div className="hc-input-frame">
        <label className="hc-message-input">
          <span className="hc-input-label">{t('Message')}</span>
          <textarea
            maxLength={5000}
            rows={creating ? 6 : 2}
            placeholder={t('How can we help?')}
            value={draft.text}
            disabled={locked}
            onChange={(e) => update({ text: e.target.value })}
          />
        </label>
        <div className="hc-compose-actions">
          <input
            ref={input}
            type="file"
            multiple
            accept="image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf"
            hidden
            onChange={(e) => {
              selectFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <button
            className="hc-attach-control"
            aria-label={t('Attach files or photos')}
            title={t('Attach files or photos')}
            disabled={locked || !config?.uploadsEnabled}
            onClick={() => input.current.click()}
          >
            <svg
              aria-hidden="true"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m21 11-8.5 8.5a6 6 0 0 1-8.5-8.5l9-9a4 4 0 0 1 5.7 5.7l-9 9a2 2 0 0 1-2.8-2.8l8.5-8.5" />
            </svg>
          </button>
          <span className="hc-spacer" />
          {canResolve && !draft.pending && (
            <button
              disabled={disabled || sending || draft.files.some((f) => f.status !== 'ready')}
              onClick={() => send(true)}
            >
              {t('Reply & resolve')}
            </button>
          )}
          <button
            className="hc-primary"
            disabled={disabled || sending || draft.files.some((f) => f.status !== 'ready')}
            onClick={() => send()}
          >
            {t(
              sending
                ? 'Sending…'
                : draft.pending
                  ? 'Retry message'
                  : creating
                    ? 'Send request'
                    : 'Send reply'
            )}
          </button>
        </div>
      </div>
      <div className="hc-compose-hint">
        <span>{t('Up to 5 files · 5 MB each · JPG, PNG, PDF')}</span>
        <span>{draft.text.length}/5000</span>
      </div>
      {draft.files.length > 0 && (
        <ul className="hc-files">
          {draft.files.map((item) => (
            <li key={item.id}>
              {item.preview && <img src={item.preview} alt={t('Selected photo')} />}
              <div>
                <strong>{item.file.name}</strong>
                <small>
                  {Math.ceil(item.file.size / 1024)} KB ·{' '}
                  {t(
                    item.status === 'ready'
                      ? 'Ready'
                      : item.status === 'failed'
                        ? 'Upload failed'
                        : 'Uploading'
                  )}
                </small>
                {['queued', 'uploading'].includes(item.status) && (
                  <progress aria-label={t('Upload progress')} value={item.progress} max="100" />
                )}
              </div>
              {item.status === 'failed' && (
                <button disabled={locked} onClick={() => enqueueUpload(item)}>
                  {t('Retry upload')}
                </button>
              )}
              <button
                disabled={locked}
                aria-label={t('Remove {name}', { name: item.file.name })}
                onClick={() => {
                  item.controller?.abort();
                  if (item.preview) URL.revokeObjectURL(item.preview);
                  update((old) => ({ ...old, files: old.files.filter((f) => f.id !== item.id) }));
                }}
              >
                {t('Remove')}
              </button>
            </li>
          ))}
        </ul>
      )}
      {!config?.uploadsEnabled && (
        <p className="hc-muted">
          {t('Attachments are temporarily unavailable. You can still send a text message.')}
        </p>
      )}
      {error && (
        <p role="alert" className="hc-error">
          {error}
        </p>
      )}
      {draft.pending && !sending && (
        <p className="hc-muted">
          {t(
            'This message is locked until its send status is confirmed. Retry uses the same message ID.'
          )}
        </p>
      )}
    </section>
  );
}
